
import { useState } from 'react';
import { useBabies } from '../../../hooks/useBabies';

interface AddActivityModalProps {
  onClose: () => void;
}

interface Activity {
  id: string;
  title: string;
  category: string;
  time: string;
  description: string;
  completed: boolean;
  date: string;
}

const categories = [
  { id: 'juego', name: 'Juego', icon: 'ri-gamepad-line', color: 'bg-blue-100 text-blue-600' },
  { id: 'cuidado', name: 'Cuidado', icon: 'ri-heart-line', color: 'bg-pink-100 text-pink-600' },
  { id: 'desarrollo', name: 'Desarrollo', icon: 'ri-book-line', color: 'bg-green-100 text-green-600' },
  { id: 'salud', name: 'Salud', icon: 'ri-health-book-line', color: 'bg-red-100 text-red-600' },
  { id: 'otro', name: 'Otro', icon: 'ri-more-line', color: 'bg-gray-100 text-gray-600' }
];

export default function AddActivityModal({ onClose }: AddActivityModalProps) {
  const { activeBaby } = useBabies();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    time: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeBaby || !formData.title || !formData.category) return;

    setIsSubmitting(true);

    try {
      const selectedDate = formData.date;
      const currentTime = formData.time || new Date().toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      const newActivity: Activity = {
        id: Date.now().toString(),
        title: formData.title,
        category: formData.category,
        time: currentTime,
        description: formData.description,
        completed: false,
        date: selectedDate
      };

      // Guardar en localStorage por bebé y fecha seleccionada
      const storageKey = `activities_${activeBaby.id}_${selectedDate}`;
      const existingActivities = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedActivities = [...existingActivities, newActivity];
      
      console.log('Guardando actividad:', newActivity);
      console.log('Storage key:', storageKey);
      console.log('Actividades actualizadas:', updatedActivities);
      
      localStorage.setItem(storageKey, JSON.stringify(updatedActivities));

      // Disparar evento personalizado para actualizar el timeline
      console.log('Disparando evento activitiesUpdated');
      window.dispatchEvent(new CustomEvent('activitiesUpdated'));

      onClose();
    } catch (error) {
      console.error('Error al agregar actividad:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Agregar Actividad</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título de la actividad *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Ej: Tiempo de juego con sonajeros"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Categoría *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleInputChange('category', category.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.category === category.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full ${category.color} flex items-center justify-center mx-auto mb-2`}>
                      <i className={`${category.icon} text-sm`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Fecha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de la actividad *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Selecciona el día en que ocurrió la actividad
              </p>
            </div>

            {/* Hora */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hora (opcional)
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si no especificas, se usará la hora actual
              </p>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Detalles adicionales sobre la actividad..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.title || !formData.category}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  'Agregar Actividad'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
